import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../services/api";
import {
  awinApi,
  externalEventsApi,
  externalLinkProps,
  withAwinClickRef,
} from "../services/externalPlatformApi";

const DESTINATIONS = ["Ibiza", "Marbella", "London", "Malta", "Amsterdam"];
const LOCAL_EXTERNAL_EVENTS_URL = "/data/external-events-seed.json";
const MONTH_ORDER = ["May", "Jun", "Jul", "Aug", "Sep", "Oct"];
const VIEW_MODES = [
  { id: "club", label: "By club" },
  { id: "date", label: "By date" },
];
const TIME_FILTERS = [
  { id: "all", label: "Show all" },
  { id: "day", label: "Daytime" },
  { id: "night", label: "Nighttime" },
];
const PRIORITY_VENUES = [
  "Ushuaia",
  "Ushuaïa",
  "Ushuaia Ibiza",
  "Ushuaïa Ibiza",
  "[UNVRS]",
  "UNVRS",
  "Pacha Ibiza",
  "Hi Ibiza",
  "Hï Ibiza",
  "Amnesia",
  "Akasha",
  "Destino",
  "O Beach Ibiza",
  "Chinois Ibiza",
  "Cova Santa",
  "Marbella Arena",
  "Mogli",
  "TIBU",
  "Pangea",
  "Mirage",
  "Ocean Club",
  "Nao Pool Club",
];

const eventName = (event) => event.event_name || event.eventName || event.title || "Event TBC";
const eventVenue = (event) => event.venue || event.location || "Venue TBC";
const eventDate = (event) => event.event_date || event.eventDate || event.date || "";

const parseDateOnly = (value) => {
  if (!value) return null;
  const isoDate = String(value).slice(0, 10);
  const match = isoDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) {
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  parsed.setHours(0, 0, 0, 0);
  return parsed;
};

const startOfToday = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

const isTodayOrFutureEvent = (event) => {
  const date = parseDateOnly(eventDate(event));
  return date ? date >= startOfToday() : true;
};

const sortableDateValue = (event) => {
  const date = parseDateOnly(eventDate(event));
  return date ? date.getTime() : Number.MAX_SAFE_INTEGER;
};

const dateLabel = (value) => {
  if (!value) return "Date TBC";
  const date = parseDateOnly(value);
  if (!date) return value;
  return date.toLocaleDateString(undefined, {
    weekday: "short",
    day: "2-digit",
    month: "short",
  });
};

const dateButtonLabel = (value, includeMonth = false) => {
  if (!value) return "TBC";
  const date = parseDateOnly(value);
  if (!date) return value;
  return date.toLocaleDateString(undefined, {
    weekday: "short",
    ...(includeMonth ? { month: "short" } : {}),
    day: "2-digit",
  });
};

const normaliseList = (payload, key) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.[key])) return payload[key];
  return [];
};

async function fetchLocalExternalEvents({ destination }) {
  const response = await fetch(LOCAL_EXTERNAL_EVENTS_URL, { cache: "force-cache" });
  if (!response.ok) throw new Error("Local external event catalogue unavailable.");
  const events = await response.json();

  return {
    events: normaliseList(events, "events").filter((event) => {
      if (event.active === false) return false;
      return !destination || event.destination === destination;
    }),
    source: "local",
  };
}

const monthFromDate = (value) => {
  const date = parseDateOnly(value);
  if (!date) return "TBC";
  return date.toLocaleDateString(undefined, { month: "short" });
};

const mapInHouseEvent = (event) => ({
  id: event.id,
  destination: "London",
  month: monthFromDate(event.date),
  event_date: event.date,
  venue: event.location || "London",
  event_name: event.title,
  artists: event.description || "HLT in-house event",
  time_slot: event.startTime ? `${event.startTime}${event.endTime ? ` - ${event.endTime}` : ""}` : "Time TBC",
  price: event.standardPrice ? `From GBP ${event.standardPrice}` : "Price TBC",
  ticket_partner: "HLT Checkout",
  hlt_essential: true,
  internal_purchase: true,
});

const monthRank = (value) => {
  const index = MONTH_ORDER.indexOf(value);
  return index === -1 ? MONTH_ORDER.length : index;
};

const defaultMonthForEvents = (items) => {
  const available = new Set(items.map((event) => event.month).filter(Boolean));
  const currentMonth = new Date().toLocaleDateString(undefined, { month: "short" });
  if (available.has(currentMonth)) return currentMonth;
  return MONTH_ORDER.find((name) => available.has(name)) || "all";
};

const startHour = (timeSlot) => {
  const match = String(timeSlot || "").match(/\b(\d{1,2})(?::\d{2})?\b/);
  if (!match) return null;
  const hour = Number(match[1]);
  if (!Number.isFinite(hour) || hour < 0 || hour > 23) return null;
  return hour;
};

const matchesTimeFilter = (event, filter) => {
  if (filter === "all") return true;
  const hour = startHour(event.time_slot || event.timeSlot);
  if (hour === null) return true;
  const isNight = hour >= 20 || hour < 6;
  return filter === "night" ? isNight : !isNight;
};

const venuePriority = (venue) => {
  const normalized = String(venue || "").toLowerCase();
  const index = PRIORITY_VENUES.findIndex((name) => normalized.includes(name.toLowerCase()));
  return index === -1 ? PRIORITY_VENUES.length : index;
};

const groupBy = (items, getKey) => {
  const groups = new Map();
  items.forEach((item) => {
    const key = getKey(item);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(item);
  });
  return groups;
};

const choiceClass = (active, extra = "") =>
  [
    "shrink-0 border px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.14em] transition",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/80",
    active
      ? "border-brand-500 bg-brand-500 text-black shadow-[0_0_28px_rgba(212,175,55,0.2)]"
      : "border-white/15 bg-black/30 text-slate-100 hover:border-brand-500/50 hover:bg-white/[0.06]",
    extra,
  ]
    .filter(Boolean)
    .join(" ");

function EventAction({ event, affiliateCode }) {
  if (event.internal_purchase) {
    return (
      <Link to={`/purchase/${event.id}`} className="btn-primary w-full shrink-0 sm:w-auto">
        Buy tickets
      </Link>
    );
  }

  if (!event.ticket_url) return null;

  return (
    <a
      href={withAwinClickRef(event.ticket_url, affiliateCode)}
      className="btn-primary w-full shrink-0 sm:w-auto"
      {...externalLinkProps}
    >
      Get tickets
    </a>
  );
}

function EventCard({ event, affiliateCode, hideVenue = false }) {
  return (
    <article className="bg-black/25 p-3 transition hover:bg-white/[0.03] sm:p-4">
      <div className="grid gap-3 md:grid-cols-[1fr,auto] md:items-start">
        <div className="min-w-0">
          <div className="flex items-center gap-2 overflow-x-auto pb-1 text-xs text-slate-400 sm:flex-wrap sm:overflow-visible sm:pb-0">
            <span className="badge">{dateLabel(eventDate(event))}</span>
            {!hideVenue && eventVenue(event) && <span className="badge">{eventVenue(event)}</span>}
            {event.time_slot && <span className="badge">{event.time_slot}</span>}
            {event.hlt_essential && (
              <span className="badge bg-brand-600/20 text-slate-100">HLT Essential</span>
            )}
          </div>

          <h4 className="mt-3 font-display text-[1.65rem] leading-none text-white md:text-3xl">
            {eventName(event)}
          </h4>

          {event.artists && (
            <p className="mt-2 text-sm leading-6 text-slate-300">
              <span className="text-brand-500">Lineup: </span>
              {event.artists}
            </p>
          )}

          <div className="mt-3 flex items-center gap-2 overflow-x-auto pb-1 text-xs text-slate-300 sm:flex-wrap sm:overflow-visible sm:pb-0">
            {event.price && <span className="badge">{event.price}</span>}
            {event.ticket_partner && <span className="badge">{event.ticket_partner}</span>}
          </div>
        </div>

        <EventAction event={event} affiliateCode={affiliateCode} />
      </div>
    </article>
  );
}

export default function ExternalDestinationsSection({ initialDestination = 'Ibiza', searchQuery = '' }) {
  const { user } = useAuth();
  const affiliateCode = user?.affiliateCode || user?.affiliate_code || user?.code;
  const [destination, setDestination] = useState(initialDestination);
  const [month, setMonth] = useState("all");
  const [venue, setVenue] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  const [timeFilter, setTimeFilter] = useState("all");
  const [viewMode, setViewMode] = useState("club");
  const [events, setEvents] = useState([]);
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function loadDestination() {
      setLoading(true);
      setError("");
      setNotice("");
      try {
        const eventRequest =
          destination === "London"
            ? api.get("/api/events/list").then(({ data }) => ({
                events: (Array.isArray(data) ? data : [])
                  .filter((event) => !event.cancelled)
                  .map(mapInHouseEvent)
                  .filter(isTodayOrFutureEvent),
              }))
            : externalEventsApi.list({ destination }).catch(() =>
                fetchLocalExternalEvents({ destination }).then((payload) => ({
                  ...payload,
                  fallback: true,
                }))
              );

        const [eventsPayload, linksPayload] = await Promise.all([
          eventRequest,
          awinApi.listLinks({ destination }).catch(() => ({ links: [], fallback: true })),
        ]);

        if (!cancelled) {
          const sorted = normaliseList(eventsPayload, "events")
            .filter((event) => event.active !== false)
            .filter((event) => !searchQuery || [eventName(event), eventVenue(event), event.artists, event.destination].join(' ').toLowerCase().includes(searchQuery.toLowerCase()))
            .filter(isTodayOrFutureEvent)
            .sort((a, b) => {
              const dateCompare = sortableDateValue(a) - sortableDateValue(b);
              if (dateCompare !== 0) return dateCompare;
              const venueCompare = eventVenue(a).localeCompare(eventVenue(b));
              if (venueCompare !== 0) return venueCompare;
              const sortA = Number(a.sort_order || a.sortOrder || 0);
              const sortB = Number(b.sort_order || b.sortOrder || 0);
              if (sortA !== sortB) return sortA - sortB;
              return eventName(a).localeCompare(eventName(b));
            });

          setEvents(sorted);
          setLinks(normaliseList(linksPayload, "links"));
          setNotice(
            eventsPayload?.fallback
              ? "Showing the bundled Ibiza/Marbella event catalogue until the backend endpoint is live."
              : ""
          );
          setMonth(defaultMonthForEvents(sorted));
          setVenue("all");
          setDateFilter("all");
          setTimeFilter("all");
          setViewMode(destination === "London" ? "date" : "club");
        }
      } catch {
        if (!cancelled) {
          setEvents([]);
          setLinks([]);
          setNotice("");
          setError("Destination data is waiting for the backend endpoint.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadDestination();
    return () => {
      cancelled = true;
    };
  }, [destination, searchQuery]);

  useEffect(() => {
    setVenue("all");
    setDateFilter("all");
  }, [destination, month]);

  const months = useMemo(() => {
    const unique = [...new Set(events.map((event) => event.month).filter(Boolean))];
    return unique.sort((a, b) => monthRank(a) - monthRank(b) || a.localeCompare(b));
  }, [events]);

  const monthCounts = useMemo(
    () =>
      events.reduce((counts, event) => {
        if (event.month) counts[event.month] = (counts[event.month] || 0) + 1;
        return counts;
      }, {}),
    [events]
  );

  const eventsInMonth = useMemo(() => {
    if (month === "all") return events;
    return events.filter((event) => event.month === month);
  }, [events, month]);

  const venueOptions = useMemo(() => {
    const counts = new Map();
    eventsInMonth.forEach((event) => {
      const name = eventVenue(event);
      if (!counts.has(name)) counts.set(name, { name, count: 0, essential: 0 });
      const entry = counts.get(name);
      entry.count += 1;
      if (event.hlt_essential) entry.essential += 1;
    });

    return [...counts.values()].sort((a, b) => {
      const priorityCompare = venuePriority(a.name) - venuePriority(b.name);
      if (priorityCompare !== 0) return priorityCompare;
      return b.count - a.count || a.name.localeCompare(b.name);
    });
  }, [eventsInMonth]);

  const dateOptions = useMemo(() => {
    const counts = new Map();
    eventsInMonth.forEach((event) => {
      const key = eventDate(event);
      if (!key) return;
      counts.set(key, (counts.get(key) || 0) + 1);
    });

    return [...counts.entries()]
      .map(([date, count]) => ({ date, count }))
      .sort((a, b) => sortableDateValue({ event_date: a.date }) - sortableDateValue({ event_date: b.date }));
  }, [eventsInMonth]);

  const visibleEvents = useMemo(
    () =>
      eventsInMonth.filter((event) => {
        if (venue !== "all" && eventVenue(event) !== venue) return false;
        if (dateFilter !== "all" && eventDate(event) !== dateFilter) return false;
        return matchesTimeFilter(event, timeFilter);
      }),
    [dateFilter, eventsInMonth, timeFilter, venue]
  );

  const groupedByVenue = useMemo(() => {
    const groups = groupBy(visibleEvents, eventVenue);
    return [...groups.entries()]
      .map(([name, groupEvents]) => ({
        name,
        events: groupEvents,
        essential: groupEvents.filter((event) => event.hlt_essential).length,
      }))
      .sort((a, b) => {
        const priorityCompare = venuePriority(a.name) - venuePriority(b.name);
        if (priorityCompare !== 0) return priorityCompare;
        return b.events.length - a.events.length || a.name.localeCompare(b.name);
      });
  }, [visibleEvents]);

  const groupedByDate = useMemo(() => {
    const groups = groupBy(visibleEvents, (event) => eventDate(event) || "Date TBC");
    return [...groups.entries()]
      .map(([date, groupEvents]) => ({ date, events: groupEvents }))
      .sort((a, b) => sortableDateValue({ event_date: a.date }) - sortableDateValue({ event_date: b.date }));
  }, [visibleEvents]);

  const needsNarrowerFilter =
    visibleEvents.length > 700 && venue === "all" && dateFilter === "all" && month === "all";

  return (
    <section className="border border-white/10 bg-[#111111]/90 p-4 shadow-card md:p-6">
      <div className="gap-4 md:flex md:items-end md:justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.28em] text-brand-500">Events and travel</p>
          <h2 className="mt-2 font-display text-5xl leading-none text-white md:text-6xl">
            Destination events
          </h2>
          <p className="mt-3 max-w-2xl text-sm leading-6 md:text-base" style={{ color: "#d7d7d7" }}>
            Browse by destination, month, club, date and daytime or nighttime programming.
            Every event keeps its lineup, venue and ticket path together.
          </p>
        </div>
        <div className="mt-5 md:mt-0">
          <p className="mb-2 text-xs uppercase tracking-[0.22em] text-slate-500 md:hidden">
            Destinations
          </p>
          <div className="-mx-4 overflow-x-auto px-4 pb-2 md:mx-0 md:overflow-visible md:px-0 md:pb-0">
            <div className="flex min-w-max gap-2 md:min-w-0 md:flex-wrap md:justify-end">
              {DESTINATIONS.map((name) => (
                <button
                  key={name}
                  type="button"
                  className={choiceClass(name === destination, "min-w-[118px] md:min-w-0 md:px-4 md:py-2")}
                  onClick={() => setDestination(name)}
                >
                  {name}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {months.length > 0 && (
        <div className="mt-5">
          <div className="mb-2 flex items-center justify-between gap-3 md:hidden">
            <p className="text-xs uppercase tracking-[0.22em] text-slate-500">Season</p>
            <p className="text-xs text-slate-500">{events.length} events</p>
          </div>
          <div className="-mx-4 overflow-x-auto px-4 pb-2 md:mx-0 md:overflow-visible md:px-0 md:pb-0">
            <div className="flex min-w-max gap-2 md:min-w-0 md:flex-wrap">
              <button
                type="button"
                className={choiceClass(
                  month === "all",
                  "flex min-w-[78px] flex-col gap-1 px-3 py-2 md:min-w-0 md:flex-row md:items-center md:gap-2"
                )}
                onClick={() => setMonth("all")}
              >
                <span>All</span>
                <span className="text-[10px] opacity-70 md:text-xs">({events.length})</span>
              </button>
              {months.map((name) => (
                <button
                  key={name}
                  type="button"
                  className={choiceClass(
                    month === name,
                    "flex min-w-[78px] flex-col gap-1 px-3 py-2 md:min-w-0 md:flex-row md:items-center md:gap-2"
                  )}
                  onClick={() => setMonth(name)}
                >
                  <span>{name}</span>
                  <span className="text-[10px] opacity-70 md:text-xs">({monthCounts[name] || 0})</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {!loading && !error && events.length > 0 && (
        <div className="mt-5 border border-white/10 bg-black/30 p-3 md:p-4">
          <div className="gap-4 md:flex md:items-start md:justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.24em] text-brand-500">Build an itinerary</p>
              <p className="mt-2 text-sm leading-6 text-slate-300">
                Pick a club first, then narrow by date or daytime/nighttime.
              </p>
            </div>
            <div className="mt-4 flex gap-2 overflow-x-auto pb-2 md:mt-0 md:overflow-visible md:pb-0">
              {VIEW_MODES.map((mode) => (
                <button
                  key={mode.id}
                  type="button"
                  className={choiceClass(viewMode === mode.id, "whitespace-nowrap px-3 py-2")}
                  onClick={() => setViewMode(mode.id)}
                >
                  {mode.label}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-4 flex gap-2 overflow-x-auto pb-2 md:flex-wrap md:overflow-visible md:pb-0">
            {TIME_FILTERS.map((filter) => (
              <button
                key={filter.id}
                type="button"
                className={choiceClass(timeFilter === filter.id, "whitespace-nowrap px-3 py-2")}
                onClick={() => setTimeFilter(filter.id)}
              >
                {filter.label}
              </button>
            ))}
          </div>

          {venueOptions.length > 0 && (
            <div className="mt-4">
              <div className="flex items-center justify-between gap-3">
                <p className="text-xs uppercase tracking-[0.22em] text-slate-400">Clubs and venues</p>
                <p className="text-xs text-slate-500">{venueOptions.length} venues</p>
              </div>
              <div className="mt-2 flex gap-2 overflow-x-auto pb-2">
                <button
                  type="button"
                  className={choiceClass(venue === "all", "whitespace-nowrap px-3 py-2")}
                  onClick={() => setVenue("all")}
                >
                  All clubs ({eventsInMonth.length})
                </button>
                {venueOptions.map((option) => (
                  <button
                    key={option.name}
                    type="button"
                    className={
                      choiceClass(venue === option.name, "max-w-[240px] whitespace-nowrap px-3 py-2")
                    }
                    title={option.name}
                    onClick={() => setVenue(option.name)}
                  >
                    <span className="truncate">
                      {option.name} ({option.count})
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {dateOptions.length > 0 && (
            <div className="mt-3">
              <div className="flex items-center justify-between gap-3">
                <p className="text-xs uppercase tracking-[0.22em] text-slate-400">Dates</p>
                <p className="text-xs text-slate-500">{dateOptions.length} active dates</p>
              </div>
              <div className="mt-2 flex gap-2 overflow-x-auto pb-2">
                <button
                  type="button"
                  className={choiceClass(dateFilter === "all", "whitespace-nowrap px-3 py-2")}
                  onClick={() => setDateFilter("all")}
                >
                  All dates
                </button>
                {dateOptions.map((option) => (
                  <button
                    key={option.date}
                    type="button"
                    className={
                      choiceClass(dateFilter === option.date, "whitespace-nowrap px-3 py-2")
                    }
                    onClick={() => setDateFilter(option.date)}
                  >
                    {dateButtonLabel(option.date, month === "all")} ({option.count})
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {loading && (
        <div className="mt-6 grid gap-4 lg:grid-cols-[1fr,280px]">
          <div className="space-y-3">
            {[...Array(3)].map((_, index) => (
              <div key={index} className="border border-white/10 bg-white/[0.03] p-4 animate-pulse">
                <div className="h-5 w-1/3 bg-white/5" />
                <div className="mt-3 h-4 w-2/3 bg-white/5" />
              </div>
            ))}
          </div>
          <div className="border border-white/10 bg-white/[0.03] p-4 animate-pulse">
            <div className="h-5 w-32 bg-white/5" />
            <div className="mt-3 h-20 bg-white/5" />
          </div>
        </div>
      )}

      {!loading && error && (
        <div className="mt-6 border border-amber-500/20 bg-amber-500/10 p-4 text-amber-100">
          {error}
        </div>
      )}

      {!loading && !error && notice && (
        <div className="mt-6 border border-brand-500/20 bg-brand-500/10 p-4 text-sm leading-6 text-slate-200">
          {notice}
        </div>
      )}

      {!loading && !error && (
        <div className="mt-6 grid gap-4 xl:grid-cols-[1fr,320px]">
          <div className="space-y-4">
            <div className="border border-white/10 bg-black/35 p-4">
              <div className="gap-3 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-xs uppercase tracking-[0.22em] text-brand-500">
                    {destination} programme
                  </p>
                  <h3 className="mt-1 font-display text-3xl leading-none text-white">
                    {visibleEvents.length} matching events
                  </h3>
                </div>
                <div className="mt-3 flex items-center gap-2 overflow-x-auto pb-1 text-xs text-slate-300 sm:mt-0 sm:flex-wrap sm:overflow-visible sm:pb-0">
                  <span className="badge">{month === "all" ? "All months" : month}</span>
                  <span className="badge">{venue === "all" ? "All clubs" : venue}</span>
                  <span className="badge">
                    {TIME_FILTERS.find((filter) => filter.id === timeFilter)?.label || "Show all"}
                  </span>
                </div>
              </div>
            </div>

            {visibleEvents.length === 0 ? (
              <div className="border border-white/10 bg-white/[0.03] p-6 text-slate-300">
                <p className="text-xs uppercase tracking-[0.24em] text-brand-500">Coming soon</p>
                <h3 className="mt-2 font-display text-3xl text-white">{destination} programme</h3>
                <p className="mt-2 text-sm leading-6">
                  This destination is ready for Harry to activate from the new admin tools.
                </p>
              </div>
            ) : needsNarrowerFilter ? (
              <div className="border border-brand-500/30 bg-brand-500/10 p-6">
                <p className="text-xs uppercase tracking-[0.24em] text-brand-500">Full season loaded</p>
                <h3 className="mt-2 font-display text-4xl leading-none text-white">
                  Choose a club or date
                </h3>
                <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-300">
                  The full {destination} season has {visibleEvents.length} events. Select a venue
                  or active date above to build a precise itinerary without flooding the page.
                </p>
              </div>
            ) : viewMode === "club" ? (
              groupedByVenue.map((group) => (
                <section key={group.name} className="border border-white/10 bg-black/35">
                  <div className="border-b border-white/10 bg-white/[0.03] p-4">
                    <div className="gap-3 sm:flex sm:items-center sm:justify-between">
                      <div>
                        <p className="text-xs uppercase tracking-[0.22em] text-brand-500">Club</p>
                        <h3 className="mt-1 font-display text-3xl leading-none text-white sm:text-4xl">
                          {group.name}
                        </h3>
                      </div>
                      <div className="mt-3 flex items-center gap-2 overflow-x-auto pb-1 text-xs text-slate-300 sm:mt-0 sm:flex-wrap sm:overflow-visible sm:pb-0">
                        <span className="badge">{group.events.length} events</span>
                        {group.essential > 0 && (
                          <span className="badge bg-brand-600/20 text-slate-100">
                            {group.essential} HLT Essential
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="divide-y divide-white/10">
                    {group.events.map((event) => (
                      <EventCard
                        key={event.id}
                        event={event}
                        affiliateCode={affiliateCode}
                        hideVenue
                      />
                    ))}
                  </div>
                </section>
              ))
            ) : (
              groupedByDate.map((group) => (
                <section key={group.date} className="border border-white/10 bg-black/35">
                  <div className="border-b border-white/10 bg-white/[0.03] p-4">
                    <div className="gap-3 sm:flex sm:items-center sm:justify-between">
                      <div>
                        <p className="text-xs uppercase tracking-[0.22em] text-brand-500">Date</p>
                        <h3 className="mt-1 font-display text-3xl leading-none text-white sm:text-4xl">
                          {dateLabel(group.date)}
                        </h3>
                      </div>
                      <span className="badge mt-3 sm:mt-0">{group.events.length} events</span>
                    </div>
                  </div>
                  <div className="divide-y divide-white/10">
                    {group.events.map((event) => (
                      <EventCard key={event.id} event={event} affiliateCode={affiliateCode} />
                    ))}
                  </div>
                </section>
              ))
            )}
          </div>

          <aside className="border border-white/10 bg-black/35 p-4 xl:sticky xl:top-24 xl:self-start">
            <h3 className="font-display text-3xl leading-none text-white">{destination} travel</h3>
            <p className="mt-2 text-sm leading-6 text-slate-400">
              Hotels, villas, transfers, experiences and VIP extras. If you want the perfect {destination}{" "}experience this
              summer, contact our concierge team and we will get back to you within 24 hours with a
              personalised itinerary. In the meantime, check our curated hotel picks below.
            </p>

            <div className="mt-4 space-y-3">
              {links.length === 0 ? (
                <p className="text-sm text-slate-300">No travel links added yet.</p>
              ) : (
                links.map((link) => (
                  <a
                    key={link.id}
                    href={withAwinClickRef(link.awin_url || link.awinUrl, affiliateCode)}
                    className="block border border-white/10 bg-white/[0.03] p-3 hover:border-brand-500/40 hover:bg-white/[0.06]"
                    {...externalLinkProps}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="truncate font-medium text-white">
                          {link.display_title || link.displayTitle}
                        </div>
                        {(link.display_subtitle || link.displaySubtitle || link.category) && (
                          <div className="mt-1 truncate text-xs text-slate-400">
                            {link.display_subtitle || link.displaySubtitle || link.category}
                          </div>
                        )}
                      </div>
                      <span className="text-brand-500">-&gt;</span>
                    </div>
                  </a>
                ))
              )}
            </div>
          </aside>
        </div>
      )}
    </section>
  );
}
